import axios from "axios";

/**
 * Vuex module: Debtors Activity Summary
 * Fetches summarized debtor activity over a date range from GET /api/v2/reports/debtors/activity
 * Headers: group (required), DB (optional)
 * Query: from, to (yyyy-MM-dd HH:mm:ss), optional: name, tpin, contact, limit (1..500)
 */
const state = () => ({
  loading: false,
  error: null,
  items: [],
  lastQuery: null,
});

const getters = {
  debtorsActivityItems: (s) => s.items,
  debtorsActivityLoading: (s) => s.loading,
  debtorsActivityError: (s) => s.error,
  debtorsActivityTotals: (s) => {
    const t = { starting: 0, bought: 0, paid: 0, closing: 0 };
    (s.items || []).forEach((i) => {
      t.starting += Number(i.startingBalance || 0);
      t.bought += Number(i.bought || 0);
      t.paid += Number(i.paid || 0);
      t.closing += Number(i.closingBalance || 0);
    });
    return t;
  },
};

const mutations = {
  SET_LOADING(s, v) { s.loading = !!v; },
  SET_ERROR(s, e) { s.error = e; },
  SET_ITEMS(s, arr) { s.items = Array.isArray(arr) ? arr : []; },
  SET_LAST_QUERY(s, q) { s.lastQuery = q; },
};

const actions = {
  /**
   * Fetch debtor activity list.
   * @param commit
   * @param {Object} payload
   * @param {string} payload.from
   * @param {string} payload.to
   * @param {string} [payload.name]
   * @param {string} [payload.tpin]
   * @param {string} [payload.contact]
   * @param {number} [payload.limit]
   */
  async fetch({ commit }, payload) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const base = window.reportsApi || '';
      const url = base + 'reports/debtors/activity';

      // Resolve headers from globals/localStorage similar to other modules
      // Build headers with safe base64 encoding for `group` and `DB`
      const isBase64 = (str) => {
        try {
          if (typeof str !== 'string' || !str.length) return false;
          // Basic base64 pattern with padding
          const re = /^(?:[A-Za-z0-9+\/]{4})*(?:[A-Za-z0-9+\/]{2}==|[A-Za-z0-9+\/]{3}=)?$/;
          if (!re.test(str)) return false;
          // Validate round-trip to avoid false positives
          const dec = window.atob(str);
          // Re-encode and compare trimming potential padding differences
          return window.btoa(dec) === str;
        } catch (e) { return false; }
      };

      const encode = (val) => (val == null ? val : (isBase64(val) ? val : window.btoa(String(val))));

      let headers = {};
      if (window.group) headers['group'] = encode(window.group);
      // Prefer selected_store from persisted state like other actions
      try {
        const sel = JSON.parse(localStorage.getItem('selected_store'));
        const storeDB = sel && sel.StoreDB ? sel.StoreDB : null;
        if (!headers['group'] && sel && sel.Group) headers['group'] = encode(sel.Group);
        if (storeDB) headers['DB'] = encode(storeDB);
      } catch (e) {
        if (window.storeDB) headers['DB'] = encode(window.storeDB); // fallback if provided globally
      }

      const lim = Math.max(1, Math.min(500, Number(payload && payload.limit) || 100));
      const params = { ...payload, limit: lim };

      const { data } = await axios.get(url, { headers, params });
      commit('SET_ITEMS', data);
      commit('SET_LAST_QUERY', params);
    } catch (e) {
      console.error('DebtorsActivity.fetch failed', e);
      commit('SET_ERROR', e?.response?.data?.message || e.message || 'Failed to load debtor activity');
      commit('SET_ITEMS', []);
    } finally {
      commit('SET_LOADING', false);
    }
  },
};

export default { namespaced: true, state, getters, mutations, actions };
